export interface OfferChange {
  field: string
  label?: string
  before: unknown
  after: unknown
}

export interface OfferChangeSummary {
  field: string
  label: string
  headline: string
  details: string[]
}

export const usesRichOfferPreview = (action: unknown): boolean => (
  action === 'offer_update' || action === 'offer_regenerate'
)

export const needsOfferDetailForRichPreview = (metadata: Record<string, any> | undefined): boolean => Boolean(
  metadata?.offer_id
  && (
    metadata.awaiting_offer_update_instructions
    || metadata.regeneration_quality_analysis
    || (metadata.requires_confirmation && usesRichOfferPreview(metadata.action))
  )
)

const PREVIEW_FIELDS = [
  'job_title', 'department', 'contract_type', 'experience_level',
  'education_level', 'description_text', 'requirements', 'main_missions',
  'soft_skills', 'benefits', 'languages', 'certifications',
  'remote_allowed',
]

const COLLECTION_NOUNS: Record<string, [string, string]> = {
  requirements: ['compétence requise', 'compétences requises'],
  main_missions: ['responsabilité', 'responsabilités'],
  soft_skills: ['compétence comportementale', 'compétences comportementales'],
  benefits: ['avantage', 'avantages'],
  languages: ['langue', 'langues'],
  certifications: ['certification', 'certifications'],
}

const isEmpty = (value: unknown): boolean => (
  value === null
  || value === undefined
  || value === ''
  || (Array.isArray(value) && value.length === 0)
)

const displayValue = (value: unknown): string => {
  if (value === true) return 'Oui'
  if (value === false) return 'Non'
  if (isEmpty(value)) return 'Non renseigné'
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const item = value as Record<string, unknown>
    const name = item.language || item.name || item.label
    const level = item.level
    if (name) return level ? `${name} (${level})` : String(name)
    return Object.values(item)
      .flatMap((entry) => Array.isArray(entry) ? entry : [entry])
      .filter((entry) => ['string', 'number'].includes(typeof entry) && String(entry).trim())
      .map(String)
      .join(', ')
  }
  return String(value)
}

export const buildUpdatedOfferPreview = (
  selectedOffer: Record<string, any>,
  changes: OfferChange[],
): Record<string, any> => {
  const preview = PREVIEW_FIELDS.reduce<Record<string, any>>((result, field) => {
    if (!isEmpty(selectedOffer?.[field])) result[field] = selectedOffer[field]
    return result
  }, {})

  changes.forEach((change) => {
    const targetField = change.field === 'description' ? 'description_text' : change.field
    if (!PREVIEW_FIELDS.includes(targetField)) return
    if (isEmpty(change.after)) delete preview[targetField]
    else preview[targetField] = change.after
  })

  if (!preview.description_text && selectedOffer?.description) {
    preview.description_text = selectedOffer.description
  }
  return preview
}

/** Map an offer_update preview onto the same quality shape the regeneration résumé uses. */
export const buildQualityAnalysisFromOfferChanges = (
  changes: OfferChange[],
): { recommendations: Array<{
  field: string
  action: 'add' | 'replace' | 'remove'
  current_value: unknown
  suggested_value: unknown
}> } => {
  const recommendations = changes.flatMap((change) => {
    const field = change.field === 'description' ? 'description_text' : change.field
    const before = change.before
    const after = change.after
    const beforeEmpty = isEmpty(before)
    const afterEmpty = isEmpty(after)
    if (beforeEmpty && afterEmpty) return []

    let action: 'add' | 'replace' | 'remove' = 'replace'
    let suggestedValue: unknown = after
    if (Array.isArray(before) || Array.isArray(after)) {
      const beforeItems = Array.isArray(before) ? before : []
      const afterItems = Array.isArray(after) ? after : []
      const beforeKeys = new Set(beforeItems.map(displayValue))
      const afterKeys = new Set(afterItems.map(displayValue))
      const added = afterItems.filter((item) => !beforeKeys.has(displayValue(item)))
      const removed = beforeItems.filter((item) => !afterKeys.has(displayValue(item)))
      if (added.length && !removed.length) {
        action = 'add'
        suggestedValue = afterItems
      } else if (removed.length && !added.length) {
        action = 'remove'
        suggestedValue = removed
      } else {
        action = 'replace'
        suggestedValue = afterItems
      }
    } else if (beforeEmpty) {
      action = 'add'
      suggestedValue = after
    } else if (afterEmpty) {
      action = 'remove'
      suggestedValue = before
    }

    return [{
      field,
      action,
      current_value: before,
      suggested_value: suggestedValue,
    }]
  })

  return { recommendations }
}

export const buildOfferChangeSummary = (changes: OfferChange[]): OfferChangeSummary[] => (
  changes.map((change) => {
    const label = change.label || change.field
    if (Array.isArray(change.before) || Array.isArray(change.after)) {
      const beforeItems = (Array.isArray(change.before) ? change.before : []).map(displayValue)
      const afterItems = (Array.isArray(change.after) ? change.after : []).map(displayValue)
      let added = afterItems.filter((item) => !beforeItems.includes(item))
      let removed = beforeItems.filter((item) => !afterItems.includes(item))
      const updatedDetails: string[] = []

      if (change.field === 'languages') {
        const toLanguageMap = (items: unknown[]) => new Map(
          items
            .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
            .map((item) => [String(item.language || item.name || '').trim(), String(item.level || '').trim()]),
        )
        const beforeLanguages = toLanguageMap(Array.isArray(change.before) ? change.before : [])
        const afterLanguages = toLanguageMap(Array.isArray(change.after) ? change.after : [])
        beforeLanguages.forEach((oldLevel, language) => {
          const newLevel = afterLanguages.get(language)
          if (!language || newLevel === undefined || newLevel === oldLevel) return
          added = added.filter((item) => item !== `${language}${newLevel ? ` (${newLevel})` : ''}`)
          removed = removed.filter((item) => item !== `${language}${oldLevel ? ` (${oldLevel})` : ''}`)
          updatedDetails.push(
            `${language} : niveau mis à jour de ${oldLevel || 'non renseigné'} à ${newLevel || 'non renseigné'}`,
          )
        })
      }

      const [singular, plural] = COLLECTION_NOUNS[change.field] || ['élément', 'éléments']
      const headline = added.length && !removed.length && !updatedDetails.length
        ? `${added.length === 1 ? 'Ajout' : 'Ajouts'} : ${added.length} ${added.length === 1 ? singular : plural}`
        : removed.length && !added.length && !updatedDetails.length
          ? `${removed.length === 1 ? 'Suppression' : 'Suppressions'} : ${removed.length} ${removed.length === 1 ? singular : plural}`
          : `Mise à jour : ${label}`
      return {
        field: change.field,
        label,
        headline,
        details: [
          ...added.map((item) => `Ajouté : ${item}`),
          ...removed.map((item) => `Supprimé : ${item}`),
          ...updatedDetails,
        ],
      }
    }

    const before = displayValue(change.before)
    const after = displayValue(change.after)
    const longText = ['description', 'description_text', 'translation_en'].includes(change.field)
      || Math.max(before.length, after.length) > 120
    return {
      field: change.field,
      label,
      headline: isEmpty(change.before)
        ? `Ajout : ${label}`
        : isEmpty(change.after)
          ? `Suppression : ${label}`
          : `Mise à jour : ${label}`,
      details: longText || isEmpty(change.after)
        ? []
        : [isEmpty(change.before) ? `Ajouté : ${after}` : `De « ${before} » à « ${after} »`],
    }
  })
)
